// src/hooks/useFloodRiskModel.js
// --- UPDATE: new hook — memoised, crash-safe bridge between floodRiskService, AI predictions, and the UI
import { useMemo, useRef } from 'react';
import {
  evaluateFloodRiskModel,
  EMPTY_FLOOD_RISK_RESULT,
} from '../services/floodRiskService';

/**
 * Structural signature including AI prediction metrics and weather forcing fields.
 * When incoming telemetry or AI predictions produce identical actionable state,
 * the previous result object reference is retained to avoid redundant UI re-renders.
 */
const buildSignature = (result) => {
  const alerts = result.roadAlerts
    .map(
      (a) =>
        `${a.wellId}:${a.severity}:${a.isStale ? 1 : 0}:${a.aiPrediction?.floodRiskProbability ?? ''}:${a.aiPrediction?.estimatedMinutesToOvertopping ?? ''}:` +
        a.impactedRoads.map((r) => `${r.roadId}@${r.distanceM}`).join(',')
    )
    .join('|');
  const wells = result.criticalWells
    .map(
      (w) =>
        `${w.wellId}:${w.severity}:${w.aiPrediction?.floodRiskProbability ?? ''}:${w.aiPrediction?.estimatedMinutesToOvertopping ?? ''}`
    )
    .join('|');
  const watch = result.watchWells
    .map((w) => `${w.wellId}:${w.severity}`)
    .join('|');
  const pipes = result.highRiskPipes
    .map((p) => `${p.pipeId}@${p.distanceM}:${p.severity}`)
    .join('|');
  return `${alerts}#${wells}#${watch}#${pipes}`;
};

const safeStringify = (value) => {
  try {
    return JSON.stringify(value ?? {});
  } catch {
    return '';
  }
};

/**
 * @param {Array} assets All infrastructure assets
 * @param {Array} sensors Registered sensor telemetry devices
 * @param {Object|Map} waterLevelReadings Real-time water level readings
 * @param {Object} [options] Any FLOOD_RISK_DEFAULTS override (hazardRadiusMeters, pipeHazardRadiusMeters, …)
 * @param {Object} [aiPredictions] Real-time AI prediction map { [wellId]: { floodRiskProbability, estimatedMinutesToOvertopping, weatherInfluence, ... } }
 * @returns {Object} Model result plus `error` (null when healthy). On failure the last good
 *                   result is retained to maintain map alerts during network or payload transients.
 */
export const useFloodRiskModel = (
  assets,
  sensors,
  waterLevelReadings,
  options = {},
  aiPredictions = {}
) => {
  const lastGoodRef = useRef(EMPTY_FLOOD_RISK_RESULT);
  const lastSignatureRef = useRef(null);
  const optionsKey = safeStringify(options);
  const aiKey = safeStringify(aiPredictions);

  return useMemo(() => {
    try {
      // Merge AI weather and overtopping predictions into the model evaluation options
      const mergedOptions = {
        ...(options || {}),
        aiPredictions: aiPredictions || {},
      };

      const result = evaluateFloodRiskModel(
        assets,
        sensors,
        waterLevelReadings,
        mergedOptions.hazardRadiusMeters || 400,
        mergedOptions
      );

      const signature = buildSignature(result);
      if (
        signature === lastSignatureRef.current &&
        lastGoodRef.current !== EMPTY_FLOOD_RISK_RESULT
      ) {
        return lastGoodRef.current;
      }

      const next = { ...result, error: null };
      lastSignatureRef.current = signature;
      lastGoodRef.current = next;
      return next;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[useFloodRiskModel] evaluation failed', error);
      }
      return { ...lastGoodRef.current, error };
    }
    // optionsKey and aiKey preserve hook stability while reacting to structural updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, sensors, waterLevelReadings, optionsKey, aiKey]);
};

export default useFloodRiskModel;
// --- END UPDATE